import {Pattern, match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {aiAssistantContextStore} from '@dms/ai-assistant';
import {getTeasServerNow} from '@dms/api/base';

import type {AssistantInterestContext} from '@prompt-bridge/shared/ai-assistant-core';

import {useLatest} from 'shared/web';

import {ASSISTANT_INTEREST_CONTEXT_REFRESH_INTERVAL_MS} from '../constants/ASSISTANT_INTEREST_CONTEXT_REFRESH_INTERVAL_MS';
import {assistantInterestContextLoadingStore} from '../store/assistantInterestContextLoadingStore';

type ContextOwnerToken = object;
type CreateAssistantInterestContext = (evaluatedAt: string) => AssistantInterestContext | undefined;
type SynchronizationDependency = boolean | object | string | undefined;
type PublishLatestOptions = {
  createContext?: CreateAssistantInterestContext;
  force?: boolean;
};
type RefreshInterestContext = () => Promise<unknown>;
type SynchronizeRefState = {
  dependencies: readonly SynchronizationDependency[];
  synchronizeRef: (node: HTMLSpanElement | null) => void;
};

let activeInterestContextOwnerToken: ContextOwnerToken | undefined;

const toRenderComparableInterestContext = (interest: AssistantInterestContext | undefined) =>
  match(interest)
    .with(Pattern.nonNullable, (availableInterest) => ({
      ...availableInterest,
      evaluatedAt: undefined,
      signals: availableInterest.signals.map((signal) => ({...signal, evaluatedAt: undefined})),
    }))
    .otherwise(() => undefined);

const areRenderInterestContextsEqual = (
  left: AssistantInterestContext | undefined,
  right: AssistantInterestContext | undefined
) => equals(toRenderComparableInterestContext(left), toRenderComparableInterestContext(right));

const areSynchronizationDependenciesEqual = (
  left: readonly SynchronizationDependency[],
  right: readonly SynchronizationDependency[]
) =>
  left.length === right.length &&
  left.every((dependency, index) => Object.is(dependency, right[index]));

export function usePublishAssistantInterestContext(
  createInterestContext: CreateAssistantInterestContext,
  synchronizationDependencies: readonly SynchronizationDependency[],
  isLoading = false,
  refreshInterestContext?: RefreshInterestContext
) {
  const latestCreateInterestContextRef = useLatest(createInterestContext);
  const latestIsLoadingRef = useLatest(isLoading);
  const latestRefreshInterestContextRef = useLatest(refreshInterestContext);
  const lastPublishedRef = useRef<AssistantInterestContext | undefined>(undefined);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const unregisterSendSynchronizerRef = useRef<(() => void) | undefined>(undefined);
  const [ownerToken] = useState<ContextOwnerToken>(() => ({}));
  const [publishLatest] = useState(
    () =>
      ({
        createContext = latestCreateInterestContextRef.current,
        force = false,
      }: PublishLatestOptions = {}) => {
        const latestInterest = createContext(getTeasServerNow() ?? new Date().toISOString());

        const ownsPublishedContext = activeInterestContextOwnerToken === ownerToken;

        if (
          !force &&
          areRenderInterestContextsEqual(lastPublishedRef.current, latestInterest) &&
          ownsPublishedContext
        ) {
          return;
        }

        lastPublishedRef.current = latestInterest;
        match({
          latestInterest,
          ownsPublishedContext,
        })
          .with({latestInterest: Pattern.nullish, ownsPublishedContext: false}, () => undefined)
          .with({latestInterest: Pattern.nullish, ownsPublishedContext: true}, () => {
            activeInterestContextOwnerToken = undefined;
            aiAssistantContextStore.patchContext({interest: undefined});
          })
          .with({latestInterest: Pattern.nonNullable}, ({latestInterest: nextInterest}) => {
            activeInterestContextOwnerToken = ownerToken;
            aiAssistantContextStore.patchContext({interest: nextInterest});
          })
          .exhaustive();
      }
  );
  const [ownerRef] = useState(() => (node: HTMLDivElement | null) => {
    if (isNotNil(node)) {
      assistantInterestContextLoadingStore.setLoading(latestIsLoadingRef.current);
      publishLatest();
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = setInterval(() => {
        const refreshInterestContext = latestRefreshInterestContextRef.current;
        if (isNotNil(refreshInterestContext)) {
          const publishIfStillActive = () => {
            if (isNotNil(refreshIntervalRef.current)) {
              publishLatest();
            }
          };
          void refreshInterestContext().then(publishIfStillActive, publishIfStillActive);
          return;
        }

        publishLatest();
      }, ASSISTANT_INTEREST_CONTEXT_REFRESH_INTERVAL_MS);
      unregisterSendSynchronizerRef.current?.();
      unregisterSendSynchronizerRef.current = aiAssistantContextStore.registerSendSynchronizer(() =>
        publishLatest({force: true})
      );
      return;
    }

    clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = undefined;
    unregisterSendSynchronizerRef.current?.();
    unregisterSendSynchronizerRef.current = undefined;
    assistantInterestContextLoadingStore.setLoading(false);

    if (activeInterestContextOwnerToken === ownerToken) {
      activeInterestContextOwnerToken = undefined;
      lastPublishedRef.current = undefined;
      aiAssistantContextStore.patchContext({interest: undefined});
    }
  });
  const synchronizationStateDependencies = [...synchronizationDependencies, isLoading];
  const createSynchronizeRefState = (): SynchronizeRefState => ({
    dependencies: synchronizationStateDependencies,
    synchronizeRef: (node) => {
      if (isNotNil(node)) {
        assistantInterestContextLoadingStore.setLoading(isLoading);
        publishLatest({createContext: createInterestContext});
      }
    },
  });
  const [initialSynchronizeRefState] = useState(createSynchronizeRefState);
  const synchronizeRefStateRef = useRef<SynchronizeRefState>(initialSynchronizeRefState);

  if (
    !areSynchronizationDependenciesEqual(
      synchronizeRefStateRef.current.dependencies,
      synchronizationStateDependencies
    )
  ) {
    synchronizeRefStateRef.current = createSynchronizeRefState();
  }

  return {ownerRef, synchronizeRef: synchronizeRefStateRef.current.synchronizeRef};
}
